if (!customElements.get('set-product-selector')) {
  class SetProductSelector extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        form: 'form',
        fields: 'input[type=radio], select',
        atcBar: 'atc-bar',
        swatch: '.swatch',
        submitButton: '[name="add"]',
        cartDrawer: 'cart-drawer',
        setProduct: 'set-product',
        pdpPrice: '.js-product .js-price',
        atcBarPrice: 'atc-bar .atc-bar__price',
        setModal: '.set-modal',
        colorText: '.js-color-text',
        shippingTextWrapper: '.js-shipping-info',
        shippingPreorderDateContainer: ".js-preorder-date-container",
      };
    }

    connectedCallback() {
      this.atcBar = document.querySelector(this.selectors.atcBar);
      this.fields = this.querySelectorAll(this.selectors.fields);
      this.swatches = this.querySelectorAll(this.selectors.swatch);
      this.form = this.querySelector(this.selectors.form);
      this.submitButton = this.form.querySelector(
        this.selectors.submitButton
      );
      this.setModal = document.querySelector(this.selectors.setModal);
      this.cartDrawer = document.querySelector(
        this.selectors.cartDrawer
      );
      this.variants = JSON.parse(
        this.querySelector('[type="application/json"]').textContent
      );

      this.#updateOptions();
      this.#updateVariant();
      this.#setInitialImage();
      this.#attachEvents();
      this.#updateColorName();

      // Set initial price
      const [finalPrice, compareAtPrice] = this.#getSetPrice();
      this.#changePrice(finalPrice, compareAtPrice);

      // Set initial button state
      const setHasPreorderVariant =
        this.#checkSetColorPreorder(
          this.querySelector(
            '.product-selector__buttons input:checked'
          ).value
        );
      if (setHasPreorderVariant) {
        this.#changeButtonText(
          window.variantStrings.preOrderNow,
          true
        );

        this.#updateShippingText();
      } else {
        this.#updateShippingText(false);
      }
      const setHasUnavailableColors =
        this.#checkSetColorUnavailability(
          this.querySelector(
            '.product-selector__buttons input:checked'
          ).value
        );
      if (setHasUnavailableColors) {
        this.#disableSubmitButton();
        this.#changeButtonText(
          window.variantStrings.unavailableOptionInSet,
          false
        );
      }
    }

    #attachEvents() {
      this.form.addEventListener('submit', (event) =>
        this.#handleSubmit(event)
      );

      for (const swatch of this.swatches) {
        swatch.addEventListener('click', (event) => {
          this.#changeImage(event);
        });
      }

      for (const field of this.fields) {
        field.addEventListener('change', (event) => {
          this.#handleVariantChange(event);

          if (this.#checkSetColorPreorder(event.target.value)) {
            this.#changeButtonText(
              window.variantStrings.preOrderNow,
              true
            );

            this.#updateShippingText();
          } else {
            this.#updateShippingText(false);
          }

          if (this.#checkSetColorUnavailability(event.target.value)) {
            this.#disableSubmitButton();
            this.#changeButtonText(
              window.variantStrings.unavailableOptionInSet,
              false
            );
          } else {
            this.#enableSubmitButton();
          }
        });
      }

      /**
       * Enable atc button after the set form completes its ATC request.
       * Used when a set is added via the normal ATC.
       */
      document.addEventListener('set-form:atc-complete', (event) =>
        this.#enableSubmitButton()
      );

      // Update price based on set products' price.
      document.addEventListener(
        'set-product:price-changed',
        (event) => {
          const [finalPrice, compareAtPrice] = this.#getSetPrice();
          this.#changePrice(finalPrice, compareAtPrice);
        }
      );

      // Update pre-order state
      document.addEventListener(
        'set-product:variant-changed',
        (event) => {
          if (this.setModal.querySelector('set-product[data-preorder]')) {
            this.#changeButtonText(
              window.variantStrings.preOrderNow,
              true
            );

            this.#updateShippingText();
          } else {
            this.#changeButtonText(
              window.variantStrings.addToCart,
              true
            );

            this.#updateShippingText(false);
          }
        }
      );
    }

    /**
     * Handle currentVariant change.
     *
     * @param {Event} event
     * @returns {Void}
     */
    #handleVariantChange(event) {
      if (event.target.type === 'number') return;
      this.#updateOptions();
      this.#updateVariant();

      if (!this.currentVariant) {
        this.#disableSubmitButton();
      }

      const [finalPrice, comparePrice] = this.#getSetPrice();
      this.#changePrice(finalPrice, comparePrice);
      this.#enableSubmitButton();
      this.#changeButtonText(window.variantStrings.addToCart, true);
      this.#updateMedia();
      this.#updateURL();
      this.#updateColorName();

      document.dispatchEvent(
        new CustomEvent('product-selector:change-variant', {
          bubbles: true,
          detail: {
            name: event.target.getAttribute('name'),
            value: event.target.value,
          },
          available: true,
        })
      );
    }

    /**
     * Update options based on selected fields.
     */
    #updateOptions() {
      this.options = Array.from(
        this.querySelectorAll('input[type="radio"]:checked, select'),
        (el) => el.value
      );
    }

    /**
     * Update currentVariant property.
     */
    #updateVariant() {
      this.currentVariant = this.variants.find((variant) => {
        return !variant.options
          .map((option, index) => this.options[index] === option)
          .includes(false);
      });
    }

    #disableSubmitButton() {
      this.querySelector(this.selectors.submitButton)?.setAttribute(
        'disabled',
        true
      );
      this.querySelector(this.selectors.submitButton)?.classList.add(
        'disabled'
      );
    }

    #enableSubmitButton() {
      this.querySelector(
        this.selectors.submitButton
      )?.removeAttribute('disabled');
      this.querySelector(
        this.selectors.submitButton
      )?.classList.remove('disabled');
    }

    /**
     * Change submit button text.
     *
     * @param {String} text
     * @param {Boolean} hasPrice
     * @returns {Void}
     */
    #changeButtonText(text, hasPrice) {
      const button = this.querySelector(this.selectors.submitButton);
      if (!button) return;

      if (hasPrice) {
        const { price, compare_at_price } = this.currentVariant;
        const priceString = Shopify.formatMoney(
          price,
          Shopify.current_money_format
        );
        button.innerHTML = `${text} <small>${priceString}</small>`;
      } else {
        button.innerHTML = `${text}`;
      }
    }

    #setInitialImage() {
      const color = this.querySelector(
        '.product-selector__buttons input:checked'
      )?.value;
      color &&
        this.closest('.product')
          .querySelector('product-media')
          ?.filterByColor(color);
    }

    #changeImage(event) {
      const variantColor = event.target.getAttribute('title');

      this.closest('.product')
        .querySelector('product-media')
        ?.filterByColor(variantColor);

      this.dispatchEvent(
        new CustomEvent('set-product-selector:color-change', {
          bubbles: true,
          detail: {
            color: event.target.previousElementSibling.value,
          },
        })
      );
    }

    #getSetPrice() {
      let originalSetPrice = 0;
      let finalSetPrice = 0;

      this.setModal
        .querySelectorAll(this.selectors.setProduct)
        .forEach((product) => {
          originalSetPrice += Number(
            product.getAttribute('original-price')
          );
          finalSetPrice += Number(
            product.getAttribute('final-price')
          );
        });

      return [finalSetPrice, originalSetPrice];
    }

    #changePrice(price, compareAtPrice) {
      const mainPrice = document.querySelector(
        this.selectors.pdpPrice
      );

      mainPrice &&
        (mainPrice.innerHTML = `
        <span>${Shopify.formatMoney(
          price,
          Shopify.current_money_format
        )}</span>
        ${
          !!compareAtPrice && compareAtPrice > price
            ? `<s>${Shopify.formatMoney(
                compareAtPrice,
                Shopify.current_money_format
              )}</s>`
            : ''
        }
      `);

      document.dispatchEvent(
        new CustomEvent('product-selector:change-price', {
          bubbles: true,
          detail: {
            finalPrice: price,
            comparePrice: compareAtPrice,
          },
        })
      );
    }

    #updateMedia() {
      if (!this.currentVariant || !this.currentVariant.featured_media)
        return;

      const productMedia = document.querySelector('product-media');
      productMedia?.setActiveSlide(
        this.currentVariant.featured_media.id
      );
    }

    #updateURL() {
      if (
        !this.currentVariant ||
        !window.location.pathname.includes('products')
      ) {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      params[params.has('variant') ? 'set' : 'append'](
        'variant',
        this.currentVariant.id
      );
      window.history.replaceState(
        {},
        '',
        `${this.dataset.url}?${params.toString()}`
      );
    }

    #updateColorName() {
      const color = this.querySelector(this.selectors.colorText);
      color && (color.innerHTML = this.options[0]);
    }

    #handleSubmit(event) {
      event.preventDefault();

      this.#disableSubmitButton();

      document
        .querySelector('set-form form [type="submit"]')
        ?.click();
      return;
    }

    /**
     * Check if there are any products that don't have the selected color.
     *
     * @param {String} value
     */
    #checkSetColorUnavailability = (value) => {
      return Array.from(
        this.setModal.querySelectorAll(
          `input[value="${value}"], option[value="${value}"]`
        )
      ).some((field) => field.hasAttribute('disabled'));
    };

    /**
     * Check if there are preorder products with the selected color.
     *
     * @param {String} value
     */
    #checkSetColorPreorder = (value) => {
      return Array.from(
        this.setModal.querySelectorAll(
          `input[value="${value}"], option[value="${value}"]`
        )
      ).some((field) => field.closest('set-product').hasAttribute('data-preorder'));
    };

    #updateShippingText(preorder = true) {
      const shippingTextWrapper = document.querySelector(this.selectors.shippingTextWrapper);

      if (!shippingTextWrapper) return;

      shippingTextWrapper.classList.toggle('product__shipping-wrapper--preorder', preorder);

      if (preorder) {
        const next_available_date = this.setModal.querySelector('set-product[data-preorder]').dataset.preorderDate;
        const shippingTextPreorderDateContainer = shippingTextWrapper.querySelector(this.selectors.shippingPreorderDateContainer);

        if (shippingTextPreorderDateContainer) {
          shippingTextPreorderDateContainer.textContent = window.variantStrings.preOrderMessage.replace('[date]', next_available_date);
        }
      }

      const setHasUnavailableColors =
        this.#checkSetColorUnavailability(
          this.querySelector(
            '.product-selector__buttons input:checked'
          ).value
        );

      shippingTextWrapper.classList.toggle('hidden', setHasUnavailableColors);
    }
  }
  customElements.define('set-product-selector', SetProductSelector);
}
